/**
 * Preview de iPhone que renderiza o que o cliente vera no app mobile.
 *
 * Reage ao modo escolhido no wizard:
 *  - "fixo" (Eu defino o valor): mostra valor pre-definido com botao "Aceitar"
 *  - "escolhido_cliente": mostra slider visual com faixa min/max e botao "Continuar"
 *
 * E pra esse o consumidor pode ate variar (Step 1 vs Step 5 mensagens), entao
 * exposto como componente generico que recebe state + opcoes de variant.
 */

import { useTranslation } from "react-i18next";
import type { PlanFormState } from "../types";

interface Props {
  state: PlanFormState;
  /** mostra apenas o frame do iPhone, util pra wizard. */
  className?: string;
}

const formatBRL = (n: number) =>
  n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

function pickPreviewNumbers(state: PlanFormState) {
  const min = parseFloat(state.valorMinimo) || 300;
  const max = parseFloat(state.valorMaximo) || 10000;
  // No modo "Eu defino" (fixo), a cobranca atual eh o min (primeira cobranca),
  // sobe a cada renovacao. valorFixo eh field legado, nao usado mais aqui.
  const fixo = min;

  // Valor "selecionado" no slider (meio entre min e max), arredondado.
  const sel = Math.round((min + max) / 2);

  // Primeiro sub-plano ativo, ou mock 20 parcelas
  const firstInstallment =
    state.installments.find((i) => i.ativo !== false) ?? state.installments[0];
  const numParcelas = firstInstallment?.numParcelas ?? 20;
  const tipoLabel =
    firstInstallment?.tipo === "diario"
      ? "diarias"
      : firstInstallment?.tipo === "semanal"
        ? "semanais"
        : firstInstallment?.tipo === "quinzenal"
          ? "quinzenais"
          : firstInstallment?.tipo === "mensal"
            ? "mensais"
            : "parcelas";

  const valorReferencia = state.tipoValor === "fixo" ? fixo : sel;
  const valorParcela = valorReferencia / Math.max(1, numParcelas);

  return {
    min,
    max,
    sel,
    fixo,
    numParcelas,
    tipoLabel,
    valorReferencia,
    valorParcela,
  };
}

export function ChargePlanMobilePreview({ state, className = "" }: Props) {
  const { t } = useTranslation();
  const numbers = pickPreviewNumbers(state);
  const isClientChoice = state.tipoValor === "escolhido_cliente";

  return (
    <div className={`flex flex-col items-center gap-3 ${className}`}>
      <div className="flex items-center gap-2 text-xs font-semibold text-primary-700">
        <span>👁</span>
        <span>{t("charge_plans.preview.label_top")}</span>
      </div>
      <p className="text-center text-xs text-muted-foreground">
        {t("charge_plans.preview.subtitle")}
      </p>

      <div className="rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold text-primary-700">
        {state.tipoValor === "fixo"
          ? t("charge_plans.step1.card_fixed.title")
          : t("charge_plans.step1.card_client.title")}
      </div>

      {/* Frame do iPhone */}
      <div className="relative mx-auto h-[600px] w-[300px] rounded-[44px] border-[10px] border-slate-900 bg-slate-900 shadow-2xl">
        {/* Notch */}
        <div className="absolute left-1/2 top-0 z-10 h-6 w-32 -translate-x-1/2 rounded-b-2xl bg-slate-900" />

        {/* Tela */}
        <div className="relative h-full w-full overflow-hidden rounded-[34px] bg-card">
          {/* Status bar */}
          <div className="flex items-center justify-between px-6 pt-3 text-xs font-semibold text-foreground">
            <span>9:41</span>
            <div className="flex items-center gap-1 text-[10px]">
              <span>📶</span>
              <span>📡</span>
              <span>🔋</span>
            </div>
          </div>

          {/* Conteúdo */}
          <div className="px-5 py-6">
            {isClientChoice ? (
              <ClientChoiceView numbers={numbers} t={t} />
            ) : (
              <FixedValueView numbers={numbers} t={t} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ====== Conteúdo: Eu defino o valor (fixo) ======

function FixedValueView({
  numbers,
  t,
}: {
  numbers: ReturnType<typeof pickPreviewNumbers>;
  t: (key: string) => string;
}) {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-center gap-2 text-sm font-medium text-foreground">
        <span>📄</span>
        <span>{t("charge_plans.preview.fixed.title")}</span>
      </div>
      <h2 className="text-center text-base font-bold leading-tight text-foreground">
        {t("charge_plans.preview.fixed.headline")}
      </h2>
      <p className="text-center text-xs text-muted-foreground">
        {t("charge_plans.preview.fixed.subtitle")}
      </p>

      <div className="rounded-2xl border border-border bg-muted/30 p-4">
        <p className="text-xs text-muted-foreground">
          {t("charge_plans.preview.fixed.amount_label")}
        </p>
        <p className="text-2xl font-extrabold text-primary-700">
          {formatBRL(numbers.fixo)}
        </p>

        <div className="mt-3">
          <p className="text-xs text-muted-foreground">
            {t("charge_plans.preview.installments_label")}
          </p>
          <p className="text-sm font-bold text-primary-700">
            {numbers.numParcelas} {t(`charge_plans.preview.${numbers.tipoLabel}`)}{" "}
            {formatBRL(numbers.valorParcela)}
          </p>
        </div>

        <div className="mt-3">
          <p className="text-xs text-muted-foreground">
            {t("charge_plans.preview.first_due_label")}
          </p>
          <p className="text-sm font-medium text-foreground">10/06/2026</p>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-muted/20 p-2 text-center text-[10px] text-muted-foreground">
        ℹ {t("charge_plans.preview.fixed.disclaimer")}
      </div>

      <button
        type="button"
        disabled
        className="rounded-xl bg-primary-600 py-3 text-sm font-bold text-white opacity-95"
      >
        {t("charge_plans.preview.fixed.cta")}
      </button>
      <p className="text-center text-[11px] text-muted-foreground">
        {t("charge_plans.preview.fixed.cta_secondary")}
      </p>
    </div>
  );
}

// ====== Conteúdo: Cliente escolhe o valor (slider) ======

function ClientChoiceView({
  numbers,
  t,
}: {
  numbers: ReturnType<typeof pickPreviewNumbers>;
  t: (key: string) => string;
}) {
  const range = numbers.max - numbers.min;
  const pct = range > 0 ? ((numbers.sel - numbers.min) / range) * 100 : 50;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-center gap-2 text-sm font-medium text-foreground">
        <span>📄</span>
        <span>{t("charge_plans.preview.client.title")}</span>
      </div>
      <h2 className="text-center text-sm font-bold text-foreground">
        {t("charge_plans.preview.client.headline")}
      </h2>
      <p className="text-center text-[11px] text-muted-foreground">
        {t("charge_plans.preview.client.subtitle")}
      </p>

      <div className="rounded-2xl border border-border bg-muted/30 p-4">
        <p className="text-center text-[11px] text-muted-foreground">
          {t("charge_plans.preview.client.selected_label")}
        </p>
        <p className="text-center text-2xl font-extrabold text-primary-700">
          {formatBRL(numbers.sel)}
        </p>

        {/* Slider visual */}
        <div className="relative mt-4 h-2 w-full overflow-visible rounded-full bg-muted">
          <div
            className="absolute left-0 top-0 h-2 rounded-full bg-gradient-to-r from-emerald-400 via-primary-500 to-primary-700"
            style={{ width: `${pct}%` }}
          />
          <div
            className="absolute -top-1.5 h-5 w-5 -translate-x-1/2 rounded-full border-2 border-primary-700 bg-white shadow"
            style={{ left: `${pct}%` }}
          />
        </div>

        <div className="mt-3 flex justify-between text-[10px] text-muted-foreground">
          <div>
            <p className="font-semibold text-foreground">
              {formatBRL(numbers.min)}
            </p>
            <p>{t("charge_plans.preview.client.min")}</p>
          </div>
          <div className="text-center">
            <p className="font-semibold text-primary-700">
              {formatBRL(numbers.sel)}
            </p>
            <p>{t("charge_plans.preview.client.selected")}</p>
          </div>
          <div className="text-right">
            <p className="font-semibold text-foreground">
              {formatBRL(numbers.max)}
            </p>
            <p>{t("charge_plans.preview.client.max")}</p>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-muted/20 p-3 text-xs">
        <p className="font-semibold text-foreground">
          {t("charge_plans.preview.summary_title")}
        </p>
        <div className="mt-2 flex justify-between text-muted-foreground">
          <span>{t("charge_plans.preview.installments_label")}</span>
          <span className="font-bold text-primary-700">
            {numbers.numParcelas} {t(`charge_plans.preview.${numbers.tipoLabel}`)}{" "}
            {formatBRL(numbers.valorParcela)}
          </span>
        </div>
        <div className="mt-1 flex justify-between text-muted-foreground">
          <span>{t("charge_plans.preview.total_label")}</span>
          <span className="font-bold text-foreground">
            {formatBRL(numbers.sel)}
          </span>
        </div>
      </div>

      <button
        type="button"
        disabled
        className="rounded-xl bg-primary-600 py-3 text-sm font-bold text-white opacity-95"
      >
        {t("charge_plans.preview.client.cta")}
      </button>
      <p className="text-center text-[11px] text-muted-foreground">
        {t("charge_plans.preview.client.cta_secondary")}
      </p>
    </div>
  );
}
